import React, { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'

// Helper functions
function getTodayString() {
  const today = new Date()
  return formatDateToString(today)
}

function getTimeFromValue(datetimeValue) {
  if (!datetimeValue) return ''
  const parts = datetimeValue.split(' ')
  return parts.length > 1 ? parts[1] : ''
}

// Normalize time to 24-hour format (HH:mm)
function normalizeTo24Hour(timeString) {
  if (!timeString) return ''
  // If already in HH:mm format, return as is
  if (/^\d{2}:\d{2}$/.test(timeString)) {
    return timeString
  }
  // If in HH:mm:ss format, extract HH:mm
  if (/^\d{2}:\d{2}:\d{2}$/.test(timeString)) {
    return timeString.substring(0, 5)
  }
  return timeString
}

// Get current time in 24-hour format (HH:mm)
function getCurrentTime() {
  const now = new Date()
  const hours = String(now.getHours()).padStart(2, '0')
  const minutes = String(now.getMinutes()).padStart(2, '0')
  return `${hours}:${minutes}`
}

function parseDateString(dateString) {
  if (!dateString) return new Date()
  const datePart = dateString.split(' ')[0] // Get only the date part
  const [year, month, day] = datePart.split('-').map(Number)
  return new Date(year, month - 1, day) // month is 0-indexed
}

function formatDateToString(date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const DateTimePicker = ({ 
  value, 
  onChange, 
  placeholder,
  className = "",
  dateOnly = false
}) => {
  // Set default placeholder based on mode
  const defaultPlaceholder = dateOnly ? "日付を選択" : "日時を選択"
  const finalPlaceholder = placeholder || defaultPlaceholder
  const [isOpen, setIsOpen] = useState(false)
  const [selectedDate, setSelectedDate] = useState(() => {
    if (value) {
      const datePart = value.split(' ')[0]
      return datePart || ''
    }
    return ''
  })
  const [selectedTime, setSelectedTime] = useState(() => {
    const time = getTimeFromValue(value) || ''
    const normalized = normalizeTo24Hour(time)
    // If no time value and in datetime mode, default to current time
    if (!normalized && !dateOnly) {
      return getCurrentTime()
    }
    return normalized
  })
  const [selectedHour, setSelectedHour] = useState(() => {
    const time = getTimeFromValue(value) || ''
    const normalized = normalizeTo24Hour(time)
    if (normalized && normalized.includes(':')) {
      return normalized.split(':')[0] || '00'
    }
    // If no time value and in datetime mode, default to current hour
    if (!normalized && !dateOnly) {
      const currentTime = getCurrentTime()
      return currentTime.split(':')[0] || '00'
    }
    return '00'
  })
  const [selectedMinute, setSelectedMinute] = useState(() => {
    const time = getTimeFromValue(value) || ''
    const normalized = normalizeTo24Hour(time)
    if (normalized && normalized.includes(':')) {
      return normalized.split(':')[1] || '00'
    }
    // If no time value and in datetime mode, default to current minute
    if (!normalized && !dateOnly) {
      const currentTime = getCurrentTime()
      return currentTime.split(':')[1] || '00'
    }
    return '00'
  })
  const [currentMonth, setCurrentMonth] = useState(() => {
    // Initialize currentMonth based on the value prop
    if (value) {
      const date = parseDateString(value)
      return new Date(date.getFullYear(), date.getMonth(), 1)
    }
    return new Date()
  })
  const datePickerRef = useRef(null)
  const buttonRef = useRef(null)
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 })
  const [showYearPicker, setShowYearPicker] = useState(false)
  const [showMonthPicker, setShowMonthPicker] = useState(false)
  
  // Get month names in Japanese
  const monthNames = ['1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月', '10月', '11月', '12月']
  
  // Generate year range (current year ± 20 years)
  const getYearRange = () => {
    const currentYear = new Date().getFullYear()
    const years = []
    for (let i = currentYear - 20; i <= currentYear + 20; i++) {
      years.push(i)
    }
    return years
  }

  const calculateDropdownPosition = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect()
      const viewportHeight = window.innerHeight
      const dropdownHeight = 400 // Approximate height of the datetime picker
      
      // Check if dropdown would be cut off at the bottom
      const spaceBelow = viewportHeight - rect.bottom
      const spaceAbove = rect.top
      
      // If not enough space below but enough space above, position above the button
      const shouldPositionAbove = spaceBelow < dropdownHeight && spaceAbove > dropdownHeight
      
      setDropdownPosition({
        top: shouldPositionAbove ? rect.top - dropdownHeight - 4 : rect.bottom + 4,
        left: rect.left,
        width: rect.width,
        positionAbove: shouldPositionAbove
      })
    }
  }

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target) &&
          buttonRef.current && !buttonRef.current.contains(event.target)) {
        setIsOpen(false)
        setShowYearPicker(false)
        setShowMonthPicker(false)
        // Clean up temporary padding
        document.body.style.paddingBottom = ''
      }
    }

    const handleResize = () => {
      if (isOpen) {
        calculateDropdownPosition()
      }
    }

    const handleScroll = () => {
      if (isOpen) {
        calculateDropdownPosition()
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      window.addEventListener('resize', handleResize)
      window.addEventListener('scroll', handleScroll, true)
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      window.removeEventListener('resize', handleResize)
      window.removeEventListener('scroll', handleScroll, true)
    }
  }, [isOpen])

  useEffect(() => {
    if (value) {
      const datePart = value.split(' ')[0]
      const timePart = getTimeFromValue(value)
      const normalizedTime = normalizeTo24Hour(timePart)
      setSelectedDate(datePart)
      
      // If no time value and in datetime mode, default to current time
      if (!normalizedTime && !dateOnly) {
        const currentTime = getCurrentTime()
        const [hour, minute] = currentTime.split(':')
        setSelectedTime(currentTime)
        setSelectedHour(hour || '00')
        setSelectedMinute(minute || '00')
      } else {
        setSelectedTime(normalizedTime)
        // Update hour and minute
        if (normalizedTime && normalizedTime.includes(':')) {
          const [hour, minute] = normalizedTime.split(':')
          setSelectedHour(hour || '00')
          setSelectedMinute(minute || '00')
        } else {
          setSelectedHour('00')
          setSelectedMinute('00')
        }
      }
      
      // Update currentMonth to match the selected date
      const date = parseDateString(datePart)
      if (!isNaN(date.getTime())) {
        setCurrentMonth(new Date(date.getFullYear(), date.getMonth(), 1))
      }
    } else {
      // Clear values when value is cleared
      setSelectedDate('')
      // In datetime mode, still show current time even when value is cleared
      if (!dateOnly) {
        const currentTime = getCurrentTime()
        const [hour, minute] = currentTime.split(':')
        setSelectedTime(currentTime)
        setSelectedHour(hour || '00')
        setSelectedMinute(minute || '00')
      } else {
        setSelectedTime('')
        setSelectedHour('00')
        setSelectedMinute('00')
      }
    }
  }, [value, dateOnly])

  // Cleanup effect for component unmount
  useEffect(() => {
    return () => {
      // Clean up temporary padding when component unmounts
      document.body.style.paddingBottom = ''
    }
  }, [])

  const formatDisplayValue = (dateString, timeString) => {
    if (!dateString) return ''
    try {
      const date = parseDateString(dateString)
      if (isNaN(date.getTime())) return ''
      
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const weekdays = ['日', '月', '火', '水', '木', '金', '土']
      const weekday = weekdays[date.getDay()]
      
      if (dateOnly) {
        // Date-only format: YYYY/MM/DD(weekday)
        return `${year}/${month}/${day}(${weekday})`
      } else {
        // DateTime format: YYYY/MM/DD(weekday) HH:mm
        if (!timeString) return ''
        return `${year}/${month}/${day}(${weekday}) ${timeString}`
      }
    } catch (error) {
      console.error('Error formatting datetime:', error)
      return ''
    }
  }

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate()
  }

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay()
  }

  const handleDateSelect = (day) => {
    const newDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day)
    const formattedDate = formatDateToString(newDate)
    setSelectedDate(formattedDate)
    
    if (dateOnly) {
      // Date-only mode: just call onChange with the date
      onChange(formattedDate)
    } else {
      // DateTime mode: If no time is set, default to current time
      let timeToUse = selectedTime
      if (!timeToUse) {
        const currentTime = getCurrentTime()
        const [hour, minute] = currentTime.split(':')
        setSelectedHour(hour)
        setSelectedMinute(minute)
        setSelectedTime(currentTime)
        timeToUse = currentTime
      }
      
      // Auto-update when date is selected
      if (timeToUse) {
        const datetimeValue = `${formattedDate} ${timeToUse}`
        onChange(datetimeValue)
      }
    }
  }

  const handleHourChange = (e) => {
    const newHour = e.target.value
    setSelectedHour(newHour)
    const newTime = `${newHour}:${selectedMinute}`
    setSelectedTime(newTime)
    // Auto-update when time is changed - only if date is also set
    if (selectedDate && newTime) {
      const datetimeValue = `${selectedDate} ${newTime}`
      onChange(datetimeValue)
    }
  }

  const handleMinuteChange = (e) => {
    const newMinute = e.target.value
    setSelectedMinute(newMinute)
    const newTime = `${selectedHour}:${newMinute}`
    setSelectedTime(newTime)
    // Auto-update when time is changed - only if date is also set
    if (selectedDate && newTime) {
      const datetimeValue = `${selectedDate} ${newTime}`
      onChange(datetimeValue)
    }
  }

  const handleMonthChange = (direction) => {
    const newMonth = new Date(currentMonth)
    if (direction === 'prev') {
      newMonth.setMonth(newMonth.getMonth() - 1)
    } else {
      newMonth.setMonth(newMonth.getMonth() + 1)
    }
    setCurrentMonth(newMonth)
  }

  const handleYearSelect = (year) => {
    const newMonth = new Date(currentMonth)
    newMonth.setFullYear(year)
    setCurrentMonth(newMonth)
    setShowYearPicker(false)
  }

  const handleMonthSelect = (month) => {
    const newMonth = new Date(currentMonth)
    newMonth.setMonth(month)
    setCurrentMonth(newMonth)
    setShowMonthPicker(false)
  }

  const handleTodayClick = () => {
    const today = getTodayString()
    setSelectedDate(today)
    
    if (dateOnly) {
      // Date-only mode: just set the date
      onChange(today)
    } else {
      // DateTime mode: set date and current time
      const currentTime = getCurrentTime()
      const [hour, minute] = currentTime.split(':')
      setSelectedHour(hour)
      setSelectedMinute(minute)
      setSelectedTime(currentTime)
      const datetimeValue = `${today} ${currentTime}`
      onChange(datetimeValue)
    }
    
    setIsOpen(false)
    setShowYearPicker(false)
    setShowMonthPicker(false)
    const todayDate = new Date()
    setCurrentMonth(new Date(todayDate.getFullYear(), todayDate.getMonth(), 1))
  }

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentMonth)
    const firstDay = getFirstDayOfMonth(currentMonth)
    const days = []

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="w-8 h-8 flex items-center justify-center"></div>)
    }

    // Add cells for each day of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const isSelected = selectedDate && day === parseInt(selectedDate.split('-')[2])
      const isToday = day === new Date().getDate() && 
                     currentMonth.getMonth() === new Date().getMonth() && 
                     currentMonth.getFullYear() === new Date().getFullYear()

      days.push(
        <button
          key={day}
          onClick={() => handleDateSelect(day)}
          className={`w-8 h-8 rounded-full text-sm transition-colors flex items-center justify-center ${
            isSelected 
              ? 'bg-blue-600 text-white' 
              : isToday 
                ? 'bg-gray-200 text-gray-800' 
                : 'hover:bg-gray-100'
          }`}
          data-testid={`datetime-picker-day-${day}`}
        >
          {day}
        </button>
      )
    }

    return days
  }

  const handleButtonClick = () => {
    if (!isOpen) {
      calculateDropdownPosition()
      
      // If no time is set, default to current time (only for datetime mode)
      if (!dateOnly && !selectedTime && selectedDate) {
        const currentTime = getCurrentTime()
        const [hour, minute] = currentTime.split(':')
        setSelectedHour(hour)
        setSelectedMinute(minute)
        setSelectedTime(currentTime)
        // Update the value with current time
        const datetimeValue = `${selectedDate} ${currentTime}`
        onChange(datetimeValue)
      }
      
      // Ensure page is scrollable when dropdown opens
      const bodyHeight = document.body.scrollHeight
      const viewportHeight = window.innerHeight
      if (bodyHeight <= viewportHeight) {
        // Add temporary padding to make page scrollable
        document.body.style.paddingBottom = '500px'
      }
    } else {
      // Remove temporary padding when closing
      document.body.style.paddingBottom = ''
    }
    setIsOpen(!isOpen)
  }

  return (
    <div className={`relative ${className}`}>
      <button
        ref={buttonRef}
        type="button"
        onClick={handleButtonClick}
        className="w-full border border-gray-300 rounded px-3 py-2 text-left bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
        data-testid="datetime-picker-button"
      >
        {formatDisplayValue(selectedDate, selectedTime) || finalPlaceholder}
      </button>

      {isOpen && createPortal(
        <div 
          className="fixed inset-0 z-40"
          style={{ pointerEvents: 'none' }}
        >
          <div 
            ref={datePickerRef}
            className="absolute z-50 bg-white border border-gray-300 rounded-lg shadow-lg p-4 min-w-[280px]"
            style={{
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              width: Math.max(dropdownPosition.width, 280),
              pointerEvents: 'auto'
            }}
            data-testid="datetime-picker-calendar"
          >
          {/* Calendar Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleMonthChange('prev')}
                className="p-1 hover:bg-gray-100 rounded"
                data-testid="datetime-picker-prev-month"
                disabled={showYearPicker || showMonthPicker}
              >
                ←
              </button>
              <button
                onClick={handleTodayClick}
                className="px-2 py-1 text-xs text-blue-600 hover:bg-blue-50 rounded transition-colors"
                data-testid="datetime-picker-today-button"
                disabled={showYearPicker || showMonthPicker}
              >
                今日
              </button>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  setShowYearPicker(!showYearPicker)
                  setShowMonthPicker(false)
                }}
                className="px-2 py-1 hover:bg-gray-100 rounded font-semibold text-lg"
                disabled={showMonthPicker}
                data-testid="datetime-picker-year-button"
              >
                {currentMonth.getFullYear()}年
              </button>
              <button
                onClick={() => {
                  setShowMonthPicker(!showMonthPicker)
                  setShowYearPicker(false)
                }}
                className="px-2 py-1 hover:bg-gray-100 rounded font-semibold text-lg"
                disabled={showYearPicker}
                data-testid="datetime-picker-month-button"
              >
                {currentMonth.getMonth() + 1}月
              </button>
            </div>
            <button
              onClick={() => handleMonthChange('next')}
              className="p-1 hover:bg-gray-100 rounded"
              data-testid="datetime-picker-next-month"
              disabled={showYearPicker || showMonthPicker}
            >
              →
            </button>
          </div>

          {/* Year Picker */}
          {showYearPicker && (
            <div className="mb-4 max-h-64 overflow-y-auto border border-gray-200 rounded p-2">
              <div className="grid grid-cols-4 gap-2">
                {getYearRange().map((year) => (
                  <button
                    key={year}
                    onClick={() => handleYearSelect(year)}
                    className={`px-3 py-2 text-sm rounded hover:bg-gray-100 ${
                      year === currentMonth.getFullYear()
                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                        : 'text-gray-700'
                    }`}
                    data-testid={`datetime-picker-year-${year}`}
                  >
                    {year}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Month Picker */}
          {showMonthPicker && (
            <div className="mb-4 border border-gray-200 rounded p-2">
              <div className="grid grid-cols-3 gap-2">
                {monthNames.map((month, index) => (
                  <button
                    key={index}
                    onClick={() => handleMonthSelect(index)}
                    className={`px-3 py-2 text-sm rounded hover:bg-gray-100 ${
                      index === currentMonth.getMonth()
                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                        : 'text-gray-700'
                    }`}
                    data-testid={`datetime-picker-month-${index}`}
                  >
                    {month}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Calendar Grid - Only show when year/month pickers are closed */}
          {!showYearPicker && !showMonthPicker && (
            <>
              {/* Weekday Headers */}
              <div className="grid grid-cols-7 gap-1 mb-2">
                {['日', '月', '火', '水', '木', '金', '土'].map(day => (
                  <div key={day} className="w-8 h-8 flex items-center justify-center text-xs text-gray-500">
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar Grid */}
              <div className="grid grid-cols-7 gap-1 mb-4">
                {renderCalendar()}
              </div>

              {/* Time Selection - Only show in datetime mode */}
              {!dateOnly && (
                <div className="border-t pt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    時刻
                  </label>
                  <div className="flex items-center gap-2">
                    <select
                      value={selectedHour}
                      onChange={handleHourChange}
                      className="flex-1 border border-gray-300 rounded px-3 py-2 text-center"
                      data-testid="datetime-picker-hour-select"
                    >
                      {Array.from({ length: 24 }, (_, i) => {
                        const hour = String(i).padStart(2, '0')
                        return (
                          <option key={hour} value={hour}>
                            {hour}
                          </option>
                        )
                      })}
                    </select>
                    <span className="text-gray-500 font-medium">:</span>
                    <select
                      value={selectedMinute}
                      onChange={handleMinuteChange}
                      className="flex-1 border border-gray-300 rounded px-3 py-2 text-center"
                      data-testid="datetime-picker-minute-select"
                    >
                      {Array.from({ length: 60 }, (_, i) => {
                        const minute = String(i).padStart(2, '0')
                        return (
                          <option key={minute} value={minute}>
                            {minute}
                          </option>
                        )
                      })}
                    </select>
                  </div>
                </div>
              )}
            </>
          )}
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}

export default DateTimePicker
